import CloseIcon from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import { SxProps, Theme } from '@mui/material/styles';
import { ChangeEvent, KeyboardEvent, useEffect, useRef } from 'react';

import { mergeSx } from '@/utils/mergeSx';

import { searchBarStyles as sx } from './SearchBar.styles';

type Props = {
	fullWidth?: boolean;
	fullHeight?: boolean;
	focusOnMount?: boolean;
	value: string;
	placeholder: string;
	customSx?: {
		root: SxProps<Theme>;
		inputField?: SxProps<Theme>;
		searchIcon?: SxProps<Theme>;
		clearIcon?: SxProps<Theme>;
		searchButton?: SxProps<Theme>;
	};
	handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
	handleClear: () => void;
	handleEnterKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
};

export const SearchBar = ({
	value,
	placeholder,
	customSx,
	handleEnterKeyPress,
	handleChange,
	handleClear,
	fullWidth = false,
	fullHeight = false,
	focusOnMount = false
}: Props) => {
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (focusOnMount) {
			inputRef.current?.focus();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Box sx={mergeSx(sx.root(fullWidth, fullHeight), customSx?.root)}>
			<InputBase
				placeholder={placeholder}
				fullWidth
				inputRef={inputRef}
				value={value}
				onChange={handleChange}
				onKeyDown={handleEnterKeyPress}
				sx={mergeSx(sx.inputField, customSx?.inputField)}
				inputProps={{
					sx: sx.placeholder
				}}
				startAdornment={
					<Search sx={mergeSx(sx.searchIcon, customSx?.searchIcon)} />
				}
				endAdornment={
					<>
						{value ? (
							<IconButton
								onClick={handleClear}
								aria-label='Clear search'
								sx={mergeSx(sx.clearIcon, customSx?.clearIcon)}
							>
								<CloseIcon fontSize='small' />
							</IconButton>
						) : null}
					</>
				}
			/>
		</Box>
	);
};
